import { useState } from 'react';
import { useBlockMedia } from './useBlockMedia';
import { MediaImage } from '../components/ui';

interface DayNightData {
  dayLabel?: string;
  dayTitle?: string;
  dayMediaId?: string;
  nightLabel?: string;
  nightTitle?: string;
  nightMediaId?: string;
}

/**
 * The day/night cross-fade from the design. Both photographs are stacked and their
 * opacity is swapped, so the transition reads as one room changing character rather than
 * two images swapping. Implemented as a real toggle group with aria-pressed.
 */
export function DayNightBlock({ data }: { data: DayNightData }) {
  const [mode, setMode] = useState<'day' | 'night'>('day');
  const dayMedia = useBlockMedia(data.dayMediaId);
  const nightMedia = useBlockMedia(data.nightMediaId);
  const isDay = mode === 'day';

  return (
    <section className="sz-daynight" aria-label="Day and night at SAZERAC">
      <MediaImage
        media={dayMedia}
        alt="SAZERAC by day"
        className="sz-daynight__layer"
        placeholder="Daytime café counter"
        style={{ opacity: isDay ? 1 : 0 }}
        sizes="100vw"
      />
      <MediaImage
        media={nightMedia}
        alt="SAZERAC by night"
        className="sz-daynight__layer"
        placeholder="Nighttime lounge"
        style={{ opacity: isDay ? 0 : 1 }}
        sizes="100vw"
      />
      <div className="sz-daynight__scrim" aria-hidden="true" />

      <div className="sz-daynight__content">
        <p className="sz-eyebrow" aria-live="polite">
          {isDay ? data.dayLabel : data.nightLabel}
        </p>
        <h3 className="sz-daynight__title">{isDay ? data.dayTitle : data.nightTitle}</h3>

        <div className="sz-toggle" role="group" aria-label="Toggle day or night view">
          <button
            type="button"
            className="sz-toggle__btn"
            data-active={isDay}
            aria-pressed={isDay}
            onClick={() => setMode('day')}
          >
            Day
          </button>
          <button
            type="button"
            className="sz-toggle__btn"
            data-active={!isDay}
            aria-pressed={!isDay}
            onClick={() => setMode('night')}
          >
            Night
          </button>
        </div>
      </div>
    </section>
  );
}
